// src/context/UserContext.tsx
import { notify } from "@/components/common/Notification";
import {
  clearLocalStorage,
  getAccessToken,
  getRefreshToken,
  getTenantId,
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalTenantId,
  setLocalUserInfo,
} from "@/lib/storeManager";
import { SD_AUTH_CHANGED_EVENT, extractAuthTokens, tryRefreshAccessToken } from "@sd/lib/sdAuthSession";
import { AuthService, UserPreferencesService } from "@/services/APIs";
import { AuthRes, SocialLoginRes } from "@/utils/types/auth";
import { jwtDecode } from "jwt-decode";
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";

interface DecodedToken {
  userId: string;
  email: string;
  exp: number;
  [key: string]: any;
}

interface UserContextType {
  jwtToken: string | null;
  refreshToken: string | null;
  decodedToken: DecodedToken | null;
  isAuthenticated: boolean;
  preferencesData: any; // Added preferences data
  fetchPreferencesData: () => Promise<void>; // Function to fetch preferences data
  signup: (
    email: string,
    password: string,
    first_name: string,
    last_name: string
  ) => Promise<any>;
  login: (email: string, password: string) => Promise<AuthRes | undefined>;
  logout: () => Promise<any>;
  socialLogin: (provider: string) => Promise<SocialLoginRes | undefined>;
  forgotPassword: (email: string) => Promise<any>;
  forgotPasswordReset: (
    id: string,
    payload: {
      new_password: string;
      confirm_password: string;
    }
  ) => Promise<any>;
  resetPassword: (values: {
    current_password: string;
    new_password: string;
  }) => Promise<any>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Local state
  const [jwtToken, setJwtToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [decodedToken, setDecodedToken] = useState<DecodedToken | null>(null);
  const [preferencesData, setPreferencesData] = useState<any>(null); // Preferences data state

  // Bootstrap from localStorage; re-sync after SD login/logout (no full page reload).
  useEffect(() => {
    const syncFromStorage = () => {
      const access = getAccessToken();
      const refresh = getRefreshToken();
      if (access && refresh) {
        try {
          const decoded = jwtDecode<DecodedToken>(access);
          setJwtToken(access);
          setRefreshToken(refresh);
          setDecodedToken(decoded);
          return;
        } catch {
          const preservedTenant = getTenantId();
          clearLocalStorage();
          if (preservedTenant !== null) {
            setLocalTenantId(preservedTenant);
          }
        }
      }
      setJwtToken(null);
      setRefreshToken(null);
      setDecodedToken(null);
    };

    syncFromStorage();
    window.addEventListener(SD_AUTH_CHANGED_EVENT, syncFromStorage);
    return () => window.removeEventListener(SD_AUTH_CHANGED_EVENT, syncFromStorage);
  }, []);

  const isAuthenticated =
    Boolean(jwtToken && decodedToken) && decodedToken!.exp * 1000 > Date.now();

  // Fetch preferences data function
  const fetchPreferencesData = useCallback(async () => {
    const data = await UserPreferencesService.getUserPreferencesGrid();
    if (data) {
      setPreferencesData(data);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPreferencesData();
    }
  }, [isAuthenticated, decodedToken, refreshToken, fetchPreferencesData]);

  const scheduleRefresh = useCallback(() => {
    if (!decodedToken || !refreshToken) {
      return;
    }
    const expiresAt = decodedToken.exp * 1000;
    const timeout = expiresAt - Date.now() - 60_000;

    const runRefresh = async () => {
      const ok = await tryRefreshAccessToken();
      if (!ok) return;
      const access = getAccessToken();
      const refresh = getRefreshToken();
      if (access) {
        setJwtToken(access);
        setDecodedToken(jwtDecode<DecodedToken>(access));
      }
      if (refresh) setRefreshToken(refresh);
    };

    if (timeout <= 0) {
      void runRefresh();
      return;
    }

    const handle = window.setTimeout(() => {
      void runRefresh();
    }, timeout);

    return () => clearTimeout(handle);
  }, [decodedToken, refreshToken]);

  useEffect(() => {
    const cleanup = scheduleRefresh();
    return cleanup;
  }, [scheduleRefresh]);

  // Call token-refresh every 14 minutes; persist the new refresh token to localStorage
  useEffect(() => {
    if (!isAuthenticated) return;

    const FOURTEEN_MINUTES = 13 * 60 * 1000;

    const interval = window.setInterval(async () => {
      if (!getRefreshToken()) return;
      await tryRefreshAccessToken();
      const access = getAccessToken();
      const refresh = getRefreshToken();
      if (access) {
        setJwtToken(access);
        setDecodedToken(jwtDecode<DecodedToken>(access));
      }
      if (refresh) setRefreshToken(refresh);
    }, FOURTEEN_MINUTES);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleSignup = async (
    userEmail: string,
    userPassword: string,
    userFirstName: string,
    userLastName: string
  ) => {
    const response = await AuthService.signup({
      email: userEmail,
      password: userPassword,
      first_name: userFirstName,
      last_name: userLastName,
    });
    type TokenPayload = Record<string, unknown>;
    const payload = (response?.data ?? response) as TokenPayload;
    const { access, refresh } = extractAuthTokens(payload);

    if (response && access && refresh) {
      setJwtToken(access);
      setRefreshToken(refresh);
      const decoded = jwtDecode<DecodedToken>(access);
      setDecodedToken(decoded);
      setLocalAccessToken(access);
      setRefreshToken(refresh);
      setLocalRefreshToken(refresh);
      const {
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      } = response.user;
      setLocalUserInfo({
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      });
      fetchPreferencesData();
      return response;
    }
  };

  const handleLogin = async (userEmail: string, userPassword: string) => {
    const response = await AuthService.login({
      email: userEmail,
      password: userPassword,
    });
    if (response) {
      const body = (response?.data ?? response) as Record<string, unknown>;
      const { access, refresh } = extractAuthTokens(body);
      if (!access || !refresh) {
        return;
      }
      setJwtToken(access);
      setRefreshToken(refresh);

      const decoded = jwtDecode<DecodedToken>(access);
      setDecodedToken(decoded);
      setLocalAccessToken(access);
      setLocalRefreshToken(refresh);
      const {
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      } = response.user;
      setLocalUserInfo({
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      });
      fetchPreferencesData();
      return response;
    }
  };

  const handleForgotPassword = async (userEmail: string) => {
    const response = await AuthService.forgotPassword(userEmail);
    if (response) {
      notify.success("Reset password email sent successfully");
      return response;
    }
  };

  const handleForgotPasswordReset = async (
    id: string,
    payload: {
      new_password: string;
      confirm_password: string;
    }
  ) => {
    const response = await AuthService.forgotPasswordReset(id, payload);
    if (response) {
      notify.success("Password reset successfully");
      return response;
    }
  };

  const handleLogout = async () => {
    const response = await AuthService.logout();
    setJwtToken(null);
    setRefreshToken(null);
    setDecodedToken(null);
    const preservedTenant = getTenantId();
    clearLocalStorage();
    window.location.reload();
    if (preservedTenant !== null) {
      setLocalTenantId(preservedTenant);
    }
    return response;
  };

  const handleResetPassword = async (values: {
    current_password: string;
    new_password: string;
  }) => {
    const response = await AuthService.resetPassword(values);
    return response;
  };

  const handleSocialLogin = async (provider: string) => {
    const response = await AuthService.socialLogin(provider);
    return response;
  };

  return (
    <UserContext.Provider
      value={{
        jwtToken,
        refreshToken,
        decodedToken,
        isAuthenticated,
        preferencesData,
        fetchPreferencesData,
        signup: handleSignup,
        login: handleLogin,
        logout: handleLogout,
        socialLogin: handleSocialLogin,
        forgotPassword: handleForgotPassword,
        forgotPasswordReset: handleForgotPasswordReset,
        resetPassword: handleResetPassword,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Export the context for use in other files
export { UserContext };
